<template>
  <div class="sidebar-wrapper" tabindex="-1">
    <!--사이드바 오버레이 -->
    <div v-show="active" class="sidebar-overlay" @click.prevent="close"></div>
    <!-- 사이드바 -->
    <div :class="['sidebar-detail', {'sidebar-show': active}]">
      <!-- Loader -->
      <div v-if="loader" class="side-loader-overlay"></div>
      <div v-if="loader" class="side-loader-wrap">
        <ring-loader :border-width="'4px'"></ring-loader>
      </div>
      <!-- Notification -->
      <div class="side-notify-wrap">
        <div v-for="(noti, index) in notification"
             :class="['side-notify-bar',
             {'side-notify-warning': noti.state === 'warning'},
             {'side-notify-success': noti.state === 'success'}]">
          <i :class="['glyphicon',
                     {'glyphicon-info-sign': noti.state === 'info'},
                     {'glyphicon-ok': noti.state === 'success'},
                     {'glyphicon-warning-sign': noti.state === 'warning'}]"></i>
          <p class="side-notify-message">{{ noti.message }}</p>
          <span @click="$emit('closeNotification', index)">x</span>
        </div>
      </div>
      <!-- 사이드바 타이틀 -->
      <div class="sidebar-header">
        <button type="button" class="close" data-dismiss="alert" aria-label="Close" @click="close">
          <span aria-hidden="true">×</span>
        </button>
        <h4 class="title text-center normal">{{ title }}</h4>
      </div>
      <!-- 탭 목록 -->
      <ul v-if="tabList.length > 0" class="sidebar-tab">
        <li v-for="tab in tabList" :key="tab.id"
            :class="[{'selected': currentTab === tab.id}]"
            @click="changeTab(tab.id)">{{ tab.name }}</li>
      </ul>
      <!-- 고정항목 목록 -->
      <div v-if="staticList.length > 0" class="sidebar-static-info">
        <div class="row">
          <div v-for="info in staticList" :key="info.id" class="col-xs-6 padding-0">
            <div class="row">
              <div class="col-md-4">{{ info.title }}</div>
              <div class="col-md-8">{{ data[info.id] | nullCheck }}</div>
            </div>
          </div>
        </div>
      </div>
      <!-- 입력 폼 -->
      <form id="detailForm"
            :class="['form', 'sidebar-body', formClass]"
            method="POST"
            @submit.prevent="submitRecord">
        <div v-for="field in currentFields" :key="field.name"
             :class="['form-group', field.class]"
             :style="field.style">
          <!-- permit check icon -->
          <span v-if="field.hasOwnProperty('permit')" class="file-permit-tag">
            <i :class="['glyphicon', 'glyphicon-ok-sign',
                        {'invalid-color': field.permit === 'invalid'},
                        {'valid-color': field.permit === 'valid'}]"></i>
          </span>
          <!-- 인풋필드 라벨 -->
          <label v-if="field.text && field.type !== 'split'"
                 :class="['title-label',
                          {'invalid-color': field.permit === 'invalid'},
                          {'valid-color': field.permit === 'valid'}]">
            {{ field.text }} <small v-if="field.required" class="require-tag">*</small>
          </label>
          <div v-if="field.type" class="input-md-wrap">
            <!-- Split -->
            <div v-if="field.type === 'split'" class="splitter">{{ field.text }}</div>
            <!-- Text -->
            <input v-if="field.type === 'text'"
                   type="text"
                   class="hz-input-default hz-input-md input-text"
                   style="width: 100%;"
                   v-model="data[field.name]"
                   :readonly="field.readonly"
                   :placeholder="field.placeholder">
            <!-- TextArea -->
            <textarea v-if="field.type == 'textarea'"
                      :class="['hz-input-default', field.fieldClass]"
                      style="width: 100%; padding: 8px;"
                      rows="10"
                      v-model="data[field.name]"
                      :placeholder="field.placeholder"
                      :readonly="field.readonly"></textarea>
            <!-- Number -->
            <input-number v-if="field.type === 'number'"
                          :class="['hz-input-default', field.fieldClass]"
                          v-model="data[field.name]"
                          @input="changeValue(field.name, data[field.name])"
                          :disabled="checkDisabled(field.name, data[field.name])"/>
            <!-- Phone -->
            <input-phone v-if="field.type === 'phone'"
                         class="hz-input-default hz-input-md"
                         v-model="data[field.name]"
                         :placeholder="field.placeholder"
                         @input="changeValue(field.name, data[field.name])"></input-phone>
            <!-- DatePicker -->
            <date-picker v-if="field.type == 'date'"
                         data-toggle="popover"
                         data-trigger="hover"
                         data-placement="bottom"
                         :data-content="field.popover"
                         :next="field.next"
                         :disabled="field.disabled"
                         v-model="data[field.name]"
                         @input="changeValue(field.name, data[field.name])"></date-picker>
            <!-- SelectBox -->
            <drop-down-list v-if="field.type == 'select'"
                            :options="field.options"
                            :fields="field.fields"
                            :label="field.label"
                            :dataUrl="field.dataUrl"
                            :size="'hz-dropdownlist-md'"
                            v-model="data[field.name]"
                            :readonly="field.readonly"></drop-down-list>
            <!-- Address -->
            <input-address v-if="field.type == 'address'"
                           :class="field.fieldClass"
                           :multi="field.multi"
                           :valueChange="field.valueChange"
                           :defaultAddressType="field.defaultAddressType"
                           v-model="data[field.name]"></input-address>
            <!-- FileUpload -->
            <input-file v-if="field.type === 'file'"
                        :auth="field.auth"
                        :autoUpload="field.autoUpload"
                        :multiple="field.multiple"
                        :limited="field.limited"
                        :preview="field.preview"
                        :rotate="field.rotate"
                        :options="field.options"
                        :dataUrl="field.dataUrl"
                        :saveUrl="field.saveUrl"
                        :removeUrl="field.removeUrl"
                        v-model="data[field.name]"></input-file>
          </div>
        </div>
      </form>
      <!-- footer -->
      <div class="row sidebar-footer">
        <div v-for="footer in footerFields" :key="footer.id" :class="[footer.class]" :style="footer.style">
          <!-- SelectBox -->
          <drop-down-list v-if="footer.type === 'select'"
                          :label="footer.label"
                          :options="footer.options"
                          @input="callBackFunc(footer.callback)"
                          v-model="data[footer.name]"></drop-down-list>
          <!-- Btn -->
          <button v-if="footer.type === 'btn'"
                  type="button" style="width: 100%;"
                  :class="['hz-btn-lg', footer.fieldClass]"
                  @click.prevent="callBackFunc(footer.callback)">{{ footer.text }}</button>
          <!-- submit -->
          <button v-if="footer.type === 'submit'"
                  type="button" style="width: 100%;"
                  :class="['hz-btn-lg', footer.fieldClass]"
                  @click.prevent="submitRecord()">{{ footer.text }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import RingLoader from 'common/components/RingLoader'
  import DatePicker from 'common/components/DatePicker'
  import DropDownList from 'common/components/DropDownList'
  import InputNumber from 'common/components/InputNumber'
  import InputPhone from 'common/components/InputPhone'
  import InputFile from 'common/components/InputFile'
  import Loader from 'common/components/Loader'
  import InputAddress from 'common/components/InputAddress'
  import Modal from 'common/components/Modal'

  export default {
    name: 'sidebar-detail',
    components: {
      RingLoader,
      DatePicker,
      DropDownList,
      InputNumber,
      InputFile,
      InputPhone,
      Loader,
      InputAddress,
      Modal
    },
    props: {
      active: {
        type: Boolean,
        default: function () {
          return false
        }
      },
      loader: {
        type: Boolean,
        default: function () {
          return false
        }
      },
      title: {
        type: String,
        default: function () {
          return '수정'
        }
      },
      tabList: {
        type: Array,
        default: function () {
          return []
        }
      },
      staticList: {
        type: Array,
        default: function () {
          return []
        }
      },
      notification: {
        type: Array,
        default: function () {
          return []
        }
      },
      fields: {
        type: Array,
        default: function () {
          return []
        }
      },
      footerFields: {
        type: Array,
        default: function () {
          return []
        }
      },
      value: {
        type: Object,
        default: function () {
          return {}
        }
      },
      formClass: {
        type: String,
        default: function () {
          /*
          * 사이드바에 폼 영역은 총 4가지 유형이 있으며, 유형에 따라 할당되는 Height가 다릅니다.
          * 1. 'form-default': 폼영역만 있는 경우
          * 2. 'form-tab': 탭, 폼영역만 있는경우
          * 3. 'form-static': 고정정보, 폼영역만 있는경우
          * 4. 'form-all': 탭, 고정정보, 폼영역만 있는경우
           */
          return 'form-default'
        }
      }
    },
    data: function () {
      return {
        method: 'edit',
        currentTab: '',
        tabs: [],
        data: {},
        tabData: {},
        selectOptions: {},
        changeCount: 0
      }
    },
    watch: {
      'active': {
        handler: function (val) {
          if (val) {
            // 사이드바가 열릴때 스크롤을 최상단으로 위치시킨다.
            this.scrollToTop()
          }
        }
      },
      'value': {
        handler: function (val) {
          // 부모창으로부터 전달받은 데이터를 복사한다.
          this.data = val
        }
      },
      'fields': {
        handler: function (val) {
          // 필드에 기본값을 설정한다.
          this.setDefault()
        },
        deep: true
      }
    },
    computed: {
      currentFields () {
        let list = []
        const fields = this.fields
        const tabList = this.tabList

        // 탭을 사용 할 경우
        if (tabList.length > 0) {
          const tab = this.currentTab !== '' ? this.currentTab : tabList[0].id
          // 전달받은 필드목록에 탭과 현재 선택된 탭을 비교해 리스트를 만들어준다.
          list = _.filter(fields, function (o) { return o.tab === tab })
        } else {
          // 탭을 사용하지 않을 경우, 전달받은 필드목록을 그대로 넣어준다.
          list = fields
        }
        // 필드 목록 반환
        return list
      }
    },
    methods: {
      init: function () {
        const vm = this
        // 탭 목록이 있을 경우 (사용여부)
        if (vm.tabList.length > 0) {
          vm.initTab()
        }
      },
      open: function () {
        this.$emit('open')
      },
      close: function () {
        this.$emit('close')
      },
      changeValue: function (field, val) {
        const fields = this.currentFields
        const relatedField = _.find(fields, {'name': field}).related

        // 관련필드가 있을 경우에만 실행
        if (typeof relatedField !== 'undefined') {
          // 연관된 필드의 값을 초기화하는 함수
          this.updateValue(relatedField, val)
        }
      },
      updateValue: function (field, val) {
        const fields = this.currentFields
        const value = _.find(fields, {'name': field}).template

        // kendoTemplate이 지정되 있을 경우에만 실행
        if (typeof value!== 'undefined') {
          const template = kendo.template(value)
          const result = template(this.data)

          // 지정된 템플릿 조건에 따라 데이터를 초기화 한다.
          this.$set(this.data, field, result)
        }
      },
      setDefault: function () {
        const vm = this
        const data = vm.data
        const fields = vm.fields

        // default 값이 있는 필드를 찾아서 지정해준다
        if (fields.length > 0) {
          fields.forEach(function (field, i) {
            const fieldDefault = field.default
            const fieldData = data[field.name]

            // 필드목록에 지정된 기본값이 있을 경우에만 실행
            if (fieldDefault && typeof fieldDefault !== 'undefined') {
              vm.$set(vm.data, field.name, fieldDefault)
            }

            // 기본값 초기화
            if (!data) {
              if (field.type === 'date') {
                vm.$set(vm.data, field.name, null)
              }
              if (field.type === 'number') {
                vm.$set(vm.data, field.name, 0)
              }
              if (field.type === 'text' && field.type !== 'split') {
                vm.$set(vm.data, field.name, '')
              }
            }
          })
        }
      },
      setDisabled: function (name) {
        // const fields = this.currentFields
        // const value = _.find(fields, {name: name}).disabled
        // if (value) {
        //   const template = kendo.template(value)
        // }
      },
      submitRecord: function () {
        const data = $.extend(true, {}, this.data)
        this.$emit('submitRecord', data)
      },
      callBackFunc: function (name) {
        const data = $.extend(true, {}, this.data)
        // 호출 할 함수명을 받아 이벤트를 발생시킨다.
        this.$emit(name, data)
      },
      initTab: function () {
        // 탭을 사용 할 경우, 선택된 탭을 초기화 한다.
        const list = this.tabList
        const selected = _.find(list, function (o) { return o.selected })

        // 초기에 지정된 탭이 있을 경우 해당 탭에 ID를 넣고 아닐 경우 0번째 ID를 넣는다.
        this.currentTab = typeof selected !== 'undefined' ? selected.id : list[0].id
      },
      changeTab: function (id) {
        const vm = this

        // 선택된 탭에 ID를 넣어준다.
        vm.currentTab = id

        // 탭 이동간 폼에 스크롤을 최상단으로 위치 시킨다.
        vm.$nextTick(function () {
          vm.scrollToTop()
        })
      },
      scrollToTop: function () {
        const el = document.getElementById('detailForm')
        // form Element가 있을 경우, 스크롤을 최상단으로 초기화한다.
        if (el) {
          el.scrollTo(0, 0)
        }
      }
    },
    created: function () {
      const vm = this

      // 부모창으로부터 전달받은 데이터를 복사한다.
      vm.data = vm.value
    },
    mounted: function () {
      // 초기화
      this.init()
    },
    updated: function () {
      $('[data-toggle="popover"]').popover()
    }
  }
</script>

<style lang="scss" scoped>
  $sidebar-detail-width: 400px;

  .sidebar-wrapper {
    .sidebar-overlay {
      display: block;
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 99;
      overflow-x: hidden;
      overflow-y: auto;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.4);

      /* sm, xs 사이즈에선 사이드바가 전체 영역을 차지하기 때문에 none 처리 */
      @media (max-width: 991px) {
        display: none;
      }
    }

    .side-notify-wrap {
      position: absolute;
      top: 120px;
      width: 100%;
      z-index: 10;
      .side-notify-bar {
        width: calc(100% - 32px);
        margin: 0 16px;
        padding: 16px;
        color: $primary-color-v2;
        border: 1px solid $primary-color-v2;
        background-color: $hzc-white;
        box-shadow: $z-depth-01;
        .glyphicon {
          margin-right: 8px;
        }
        .side-notify-message {
          margin: 0;
          display: inline-block;
        }
        span {
          float: right;
          cursor: pointer;
          &:hover {
            font-weight: bold;
          }
        }
      }

      .side-notify-warning {
        color: #FF5747;
        border: 1px solid #ff5747;
      }

      .side-notify-success {
        color: #4a7e5c;
        border: 1px solid #4a7e5c;
      }
    }

    .sidebar-detail {
      position: fixed;
      top: 0;
      right: -$sidebar-detail-width;
      width: $sidebar-detail-width;
      height: 100%;
      background-color: $hzc-white;
      border: 0;
      box-shadow: $z-depth-01;
      z-index: 1000;
      transition: all 0.5s ease;

      @media (max-width: 991px) {
        width: 100%;
        right: -100%;
      }

      &.sidebar-show {
        right: 0;
      }

      .sidebar-header {
        height: 64px;
        padding: 20px 16px;

        .title {
          margin: 0;
        }
      }

      .sidebar-tab {
        height: 48px;
        margin: 0;
        padding: 0 16px;
        border-bottom: 1px solid $border-color;

        >li {
          display: inline-block;
          float: left;
          height: 100%;
          padding: 16px;
          color: $second-color;
          cursor: pointer;

          &:hover,
          &.selected {
            color: $primary-color-v2;
            border-bottom: 3px solid $primary-color-v2;
          }
        }
      }

      .sidebar-static-info {
        height: auto;
        padding: 16px;
        border-bottom: 1px solid $border-color;

        .col-md-4, .col-md-8 {
          padding: 8px 0;
          margin: 0;
          font-size: 14px;
          font-weight: 700;
          line-height: 1.29;
          text-align: left;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: $hzc-gray-06;

          @media (max-width: 991px) {
            padding: 4px 0;
          }
        }

        .col-md-8 {
          color: $font-color;
        }
      }

      .sidebar-body {
        overflow-y: auto;
        padding: 0 8px;

        &.form-default {
          height: calc(100% - 128px);
        }

        &.form-tab {
          height: calc(100% - 176px);
        }

        &.form-static {
          height: calc(100% - 262px);
          @media (max-width: 991px) {
            height: calc(100% - 316px);
          }
        }

        &.form-all {
          height: calc(100% - 310px);
          @media (max-width: 991px) {
            height: calc(100% - 364px);
          }
        }

        .glyphicon-ok-sign {
          color: #868E97;
        }
        .invalid-color {
          color: #FF5747;
        }

        .valid-color {
          color: #4a7e5c;
        }

        .horizontal-form-group {
          .title-label {
            width: 25%;
            float: left;
          }

          .input-md-wrap {
            width: 75%;
            float: left;
          }
        }

        .splitter {
          border: 1px solid $border-color;
          border-left-width: 0;
          border-right-width: 0;
          padding: 4px 16px;
          margin: 0 -16px;
          font-size: 12px;
          color: $second-color;
          background-color: $grey;

          .input-text {
            border: 1px solid $border-color;
          }

          &.blank {
            background-color: white;
            border: 0px;
            margin-bottom: 16px;
          }
        }
      }

      .sidebar-footer {
        height: 64px;
        padding: 8px 16px;
        background-color: $hzc-gray-03;
      }
    }

    .side-loader-overlay {
      position: absolute;
      width: 100%;
      height: 100%;
      background-color: #f5f5f5;
      opacity: 0.5;
      z-index: 3;
    }
    .side-loader-wrap {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 56px;
      height: 56px;
      transform: translate(-50%, -50%);
      z-index: 5;
    }
  }
</style>



// WEBPACK FOOTER //
// ../common/components/SidebarDetail.vue